import axios from 'axios';

// Base API configuration
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 60000, // 60s for Ollama
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle common errors
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Handle 401 Unauthorized (token expired)
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    
    // Handle network errors
    if (!error.response) {
      console.error('Network error:', error);
      error.message = 'Network error. Please check your connection.';
    }
    
    return Promise.reject(error);
  }
);

// Authentication API
export const authAPI = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  register: (userData) => api.post('/auth/register', userData),
  getProfile: () => api.get('/auth/profile'),
  updateProfile: (profileData) => api.put('/auth/profile', profileData),
  changePassword: (currentPassword, newPassword) => 
    api.put('/auth/password', { current_password: currentPassword, new_password: newPassword }),
  logout: () => api.post('/auth/logout'),
};

// Daily Check-in API
export const checkinAPI = {
  create: (checkinData) => api.post('/checkin/create', checkinData),
  getHistory: (page = 1, limit = 30) => 
    api.get(`/checkin/history?page=${page}&limit=${limit}`),
  getToday: () => api.get('/checkin/today'),
  getStreak: () => api.get('/checkin/streak'),
  update: (id, checkinData) => api.put(`/checkin/${id}`, checkinData),
  delete: (id) => api.delete(`/checkin/${id}`),
  getStats: (period = '30d') => api.get(`/checkin/stats?period=${period}`),
  getAddictionImpact: () => api.get('/checkin/addiction-impact'),
  getMoodHistory: (limit) => api.get(`/checkin/mood-history${limit ? `?limit=${limit}` : ''}`),
};

// Questionnaire API
export const questionnaireAPI = {
  getTypes: () => api.get('/questionnaire/types'),
  getQuestions: (type) => api.get(`/questionnaire/${type}`),
  submit: (responses) => api.post('/questionnaire/submit', responses),
  getHistory: (type) => api.get(`/questionnaire/history${type ? `?type=${type}` : ''}`),
  getResults: (id) => api.get(`/questionnaire/results/${id}`),
};

// Predictions API
export const predictionAPI = {
  getOrganHealth: (data) => api.post('/predictions/organ', data),
  getHabitsPrediction: (data) => api.post('/predictions/habits', data),
  getMentalHealthPrediction: (data) => api.post('/predictions/mental-health', data),
  getAddictionRisk: (data) => api.post('/predictions/addiction-risk', data),
  getPredictionsHistory: () => api.get('/predictions/history'),
};

// Reports API
export const reportAPI = {
  generate: (type, options = {}) => 
    api.post('/reports/generate', { type, ...options }),
  refresh: (type, options = {}) => 
    api.post('/reports/refresh', { type, ...options }),
  getHistory: (page = 1, limit = 10) => 
    api.get(`/reports/history?page=${page}&limit=${limit}`),
  getById: (id) => 
    api.get(`/reports/${id}`),
  getReport: (id) => api.get(`/reports/${id}`),
  downloadPDF: (id) => api.get(`/reports/${id}/pdf`, { responseType: 'blob' }),
  share: (id, isPublic) => api.put(`/reports/${id}/share`, { is_public: isPublic }),
};

// Chatbot API - Now uses AI-powered endpoint
export const chatbotAPI = {
  sendMessage: (message, sessionId) =>
    api.post('/chat', { message, session_id: sessionId }),
  getHistory: (sessionId) =>
    api.get(`/chat/history${sessionId ? `?session_id=${sessionId}` : ''}`),
  getStatus: (conversationId) => api.get(`/chat/status/${conversationId}`),
  getSession: (sessionId) => api.get(`/chat/session/${sessionId}`),
  getContext: () => api.get('/chat/context'),
  endSession: (sessionId) => api.delete(`/chat/session/${sessionId}`),
};

// Leaderboard API
export const leaderboardAPI = {
  getOverall: (period = 'all_time') => 
    api.get(`/leaderboard/overall?period=${period}`),
  getByCategory: (category, period = 'all_time') => 
    api.get(`/leaderboard/category/${category}?period=${period}`),
  getUserRank: () => api.get('/leaderboard/my-rank'),
  getAddictionImpact: async () => {
    const response = await api.get('/checkin/addiction-impact');
    return response.data;
  },
  getMyRank: () => api.get('/leaderboard/my-rank'),
  getTopUsers: (limit = 10) => api.get(`/leaderboard/top?limit=${limit}`),
};

// Feedback API
export const feedbackAPI = {
  submit: (feedbackData) => api.post('/feedback', feedbackData),
  getHistory: () => api.get('/feedback/history'),
  update: (id, feedbackData) => api.put(`/feedback/${id}`, feedbackData),
  delete: (id) => api.delete(`/feedback/${id}`),
};

// Savings Tracker API
export const savingsAPI = {
  getTrackers: () => api.get('/savings/trackers'),
  createTracker: (trackerData) => api.post('/savings/trackers', trackerData),
  updateTracker: (id, trackerData) => api.put(`/savings/trackers/${id}`, trackerData),
  deleteTracker: (id) => api.delete(`/savings/trackers/${id}`),
  getSavings: (id) => api.get(`/savings/trackers/${id}/savings`),
  updateSavings: (id, amount) => 
    api.put(`/savings/trackers/${id}/savings`, { amount }),
};

// Users API (for admin features)
export const usersAPI = {
  getProfile: () => api.get('/users/profile'),
  updateProfile: (profileData) => api.put('/users/profile', profileData),
  uploadAvatar: (formData) => 
    api.post('/users/avatar', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),
  deleteAccount: () => api.delete('/users/account'),
  getSettings: () => api.get('/users/settings'),
  updateSettings: (settings) => api.put('/users/settings', settings),
};

// User Statistics API
export const userStatsAPI = {
  getMyStats: () => api.get('/user-stats/my-stats'),
  updateStats: () => api.post('/user-stats/update-stats')
};

// Utility functions
export const apiUtils = {
  // Handle API errors consistently
  handleError: (error) => {
    if (error.response) {
      // Server responded with error status
      return {
        message: error.response.data.message || 'Server error',
        status: error.response.status,
        details: error.response.data.details || null
      };
    } else {
      // Something else happened
      return {
        message: error.message || 'Unknown error',
        status: null,
        details: null
      };
    }
  },

  // Format date for API
  formatDate: (date) => {
    return date.toISOString().split('T')[0];
  },

  // Build query string from object
  buildQueryString: (params) => {
    const query = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        query.append(key, value);
      }
    });
    return query.toString();
  }
};

// Export the base api instance and all API services
export default api;
export { api };
